import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { handleActionError, RETRY_HINT } from "@/lib/errors";
import { useLeaveVoiceMessage } from "@/queries/voice-order";
import { CheckCircle2, Loader2, Mic } from "lucide-react";
import { useCallback, useState } from "react";

import { VoiceMessageRecorderPanel } from "./VoiceMessageRecorderPanel";

interface VoiceMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The "leave a voice message" modal (KAN-192). A thin shell around
 * {@link VoiceMessageFlow} - Radix mounts the flow only while the dialog is
 * open and unmounts it on close, so every session starts from a clean
 * recorder state and closing mid-recording releases the microphone via the
 * flow's unmount cleanup (same shape as {@link VoiceOrderDialog}).
 */
export function VoiceMessageDialog({
  open,
  onOpenChange
}: VoiceMessageDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-lg">
        <VoiceMessageFlow onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

type Phase = "record" | "submitting" | "done" | "error";

const HEADER: Record<Phase, { title: string; description?: string }> = {
  record: {
    title: "Leave a voice message",
    description: "Record a short message and we'll get back to you."
  },
  submitting: { title: "Leave a voice message" },
  done: { title: "Message sent" },
  error: { title: "Leave a voice message" }
};

/**
 * The voice-message state machine, mounted only while the dialog is open.
 * Record -> submit -> done | error - simpler than {@link VoiceOrderFlow}
 * (no parse/match/review/confirm chain, since a message is stored as-is with
 * no transcription or catalog matching).
 */
function VoiceMessageFlow({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>("record");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const leaveMessage = useLeaveVoiceMessage();

  const handleRecordingComplete = useCallback(
    (blob: Blob) => {
      setSubmitError(null);
      setPhase("submitting");
      leaveMessage.mutate(blob, {
        onSuccess: () => setPhase("done"),
        onError: (error) => {
          setSubmitError(
            handleActionError(error, {
              boundary: "voice message send",
              fallback: RETRY_HINT
            })
          );
          setPhase("error");
        }
      });
    },
    [leaveMessage]
  );

  const recorder = useAudioRecorder({ onComplete: handleRecordingComplete });

  const backToRecord = useCallback(() => {
    setSubmitError(null);
    recorder.reset();
    setPhase("record");
  }, [recorder]);

  const header = HEADER[phase];

  return (
    <>
      <DialogHeader className="border-border border-b p-6 pb-4 text-left">
        <DialogTitle className="flex items-center gap-2">
          <Mic
            className="h-5 w-5 text-purple-600 dark:text-purple-400"
            aria-hidden="true"
          />
          {header.title}
        </DialogTitle>
        {header.description ? (
          <DialogDescription>{header.description}</DialogDescription>
        ) : (
          // Radix requires a description node for a labelled dialog; keep an
          // sr-only one on phases whose body carries its own copy.
          <DialogDescription className="sr-only">
            {header.title}
          </DialogDescription>
        )}
      </DialogHeader>

      {phase === "record" ? (
        <VoiceMessageRecorderPanel
          status={recorder.status}
          error={recorder.error}
          remainingMs={recorder.remainingMs}
          onStart={() => void recorder.start()}
          onStop={recorder.stop}
          onCancel={onClose}
        />
      ) : null}

      {phase === "submitting" ? (
        <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
          <Loader2
            className="h-8 w-8 animate-spin text-purple-600 dark:text-purple-400"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Sending your message…
          </p>
        </div>
      ) : null}

      {phase === "error" ? (
        <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Couldn't send your message
            </h3>
            <p className="mx-auto max-w-xs text-sm text-gray-500 dark:text-gray-400">
              {submitError}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <Button type="button" onClick={backToRecord}>
              <Mic className="mr-2 h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {phase === "done" ? (
        <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50 dark:bg-green-900/20">
            <CheckCircle2
              className="h-8 w-8 text-green-600 dark:text-green-400"
              aria-hidden="true"
            />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Message sent
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Thanks - our team will get back to you.
            </p>
          </div>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </div>
      ) : null}
    </>
  );
}
